/**
 * Card Price Service
 * Fetches and synchronizes estimated market prices for cards in the Supabase database.
 * Uses public.cards table (price, price_updated_at) to avoid redundant processing across users.
 * Updates at most once per day: if already updated that day, no processing/calls are made.
 * Uses Homura API & Kyubey proxy with USD -> BRL conversion (factor: 5).
 */

import { supabase } from '../lib/supabase';
import { 
  getGameSlug, 
  getGameApiBaseUrl, 
  GAME_ID_TO_SLUG as CENTRAL_GAME_ID_TO_SLUG 
} from '../constants/games';

export const GAME_ID_TO_SLUG: Record<string, string> = CENTRAL_GAME_ID_TO_SLUG;

export const getApiBaseUrlForSlug = (slug: string): string => {
  return getGameApiBaseUrl(slug);
};

export const resolveGameSlug = (game: number | string | undefined | null): string => {
  if (!game) return 'one-piece';
  return getGameSlug(game, 'one-piece');
};

/**
 * Checks whether a timestamp represents an update made today (or within the last 24 hours).
 * If true, the price is fresh for the day and must not trigger external API calls or DB writes.
 */
export const isPriceUpdatedToday = (priceUpdatedAt?: string | Date | null): boolean => {
  if (!priceUpdatedAt) return false;
  const date = new Date(priceUpdatedAt);
  if (isNaN(date.getTime())) return false;

  const now = new Date();
  
  // Same calendar day in local time
  const isSameLocalDay = (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );

  // Same calendar day in UTC
  const isSameUTCDay = (
    date.getUTCFullYear() === now.getUTCFullYear() &&
    date.getUTCMonth() === now.getUTCMonth() &&
    date.getUTCDate() === now.getUTCDate()
  );

  // Within the last 24 hours
  const diffMs = now.getTime() - date.getTime();
  const isWithin24Hours = diffMs >= 0 && diffMs < 24 * 60 * 60 * 1000;

  return isSameLocalDay || isSameUTCDay || isWithin24Hours;
};

export const USD_TO_BRL_RATE = 5;

/**
 * Converte valor em Dólar (USD) para Real (BRL) usando o fator multiplicador 5.
 */
export const convertUsdToBrl = (usdAmount: number | null | undefined): number => {
  if (usdAmount === undefined || usdAmount === null || isNaN(Number(usdAmount)) || Number(usdAmount) <= 0) {
    return 0;
  }
  return Math.round(Number(usdAmount) * USD_TO_BRL_RATE * 100) / 100;
};

// In-memory map and localStorage for price caching to avoid redundant external calls and keep most recent prices
export interface CachedPrice {
  price: number;
  price_updated_at: string;
}

const sessionPriceMap = new Map<string, CachedPrice>();
const LOCAL_STORAGE_PRICE_PREFIX = 'cardumy_card_price_v3:';

// Purge old versions of localStorage price cache that may contain corrupt or name-based prices
if (typeof window !== 'undefined' && window.localStorage) {
  try {
    const legacyPrefixes = ['cardumy_card_price_v2:', 'cardumy_card_price_:'];
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && legacyPrefixes.some(lp => k.startsWith(lp))) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch {
    // ignore storage errors
  }
}

export const buildCardPriceKey = (
  slug: string,
  code?: string | null,
  cardDbId?: number | string | null
): string => {
  if (cardDbId) return `id:${cardDbId}`;
  const c = (code || '').trim().toLowerCase();
  if (c && !/^\d{1,4}$/.test(c)) return `${slug}:${c}`;
  return `${slug}:unknown`;
};

export const getPriceFromCache = (
  slug: string,
  code?: string | null,
  cardDbId?: number | string | null
): CachedPrice | null => {
  const keysToTry: string[] = [];
  if (cardDbId) keysToTry.push(`id:${cardDbId}`);
  const cleanCode = (code || '').trim().toLowerCase();
  // Do not query cache by bare numbers (internal DB IDs) to avoid collisions
  if (cleanCode && !/^\d{1,4}$/.test(cleanCode)) {
    keysToTry.push(`${slug}:${cleanCode}`);
  }

  for (const k of keysToTry) {
    // 1. Verificação em memória
    const mem = sessionPriceMap.get(k);
    if (mem && isPriceUpdatedToday(mem.price_updated_at)) {
      return mem;
    }

    // 2. Verificação em localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const raw = localStorage.getItem(`${LOCAL_STORAGE_PRICE_PREFIX}${k}`);
        if (raw) {
          const parsed = JSON.parse(raw) as CachedPrice;
          if (parsed && typeof parsed.price === 'number' && isPriceUpdatedToday(parsed.price_updated_at)) {
            sessionPriceMap.set(k, parsed);
            return parsed;
          }
        }
      } catch {
        // ignora erros de leitura de storage
      }
    }
  }

  return null;
};

export const setPriceInCache = (
  keyOrSlug: string,
  price: number,
  updatedAt?: string,
  code?: string | null,
  cardDbId?: number | string | null
): void => {
  if (price === undefined || price === null || isNaN(price) || price <= 0) return;
  const roundedPrice = Math.round(price * 100) / 100;
  const nowIso = updatedAt || new Date().toISOString();
  const entry: CachedPrice = { price: roundedPrice, price_updated_at: nowIso };

  const keys: string[] = [];
  if (cardDbId) {
    keys.push(`id:${cardDbId}`);
  }
  const cleanCode = (code || '').trim().toLowerCase();
  if (cleanCode && !/^\d{1,4}$/.test(cleanCode)) {
    keys.push(`${keyOrSlug}:${cleanCode}`);
  }

  for (const k of keys) {
    sessionPriceMap.set(k, entry);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem(`${LOCAL_STORAGE_PRICE_PREFIX}${k}`, JSON.stringify(entry));
      } catch {
        // ignora possíveis erros de quota
      }
    }
  }
};

export const clearPriceCache = (): void => {
  sessionPriceMap.clear();
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith(LOCAL_STORAGE_PRICE_PREFIX) || k.startsWith('cardumy_card_price_'))) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch {
      // ignora erros
    }
  }
};

/**
 * A responsabilidade de alterar o preço das cartas no banco não é mais desta aplicação.
 * Portanto, não alteramos mais o valor no banco; gravamos apenas no cache local/sessão
 * para obter os valores mais recentes.
 */
export const updateCardPriceInDb = async (
  cardDbId?: number | string | null,
  game?: number | string | null,
  externalId?: string | null,
  price?: number
): Promise<void> => {
  if (price === undefined || price === null || isNaN(price) || price <= 0) return;
  const roundedPrice = Math.round(price * 100) / 100;
  const slug = resolveGameSlug(game);
  const code = (externalId || '').trim();

  // Atualiza apenas o cache local/sessão sem alterar o banco de dados
  setPriceInCache(slug, roundedPrice, new Date().toISOString(), code, cardDbId);
};

/**
 * Fetches card price from external Homura / Kyubey API.
 * STRICTLY uses exact code matching. Avoids all random fallbacks (no blind name or internal ID queries).
 */
export const fetchPriceFromExternalApi = async (
  slug: string,
  code: string
): Promise<number | null> => {
  const cleanCode = (code || '').trim();
  // Avoid fallbacks: empty codes or internal integer primary keys (e.g. "1", "297") must never query external API
  if (!cleanCode || /^\d{1,4}$/.test(cleanCode)) {
    return null;
  }

  const token = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_HOMURA_TOKEN) || 
                (typeof process !== 'undefined' && process.env?.VITE_HOMURA_TOKEN) || '';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const normalizedReqCode = cleanCode.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  const fetchAttempt = async (paramVal: string): Promise<number | null> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3500);

      const apiBase = getApiBaseUrlForSlug(slug);
      const url = `${apiBase}/api/${slug}/cards?code=${encodeURIComponent(paramVal)}&limit=1`;
      const res = await fetch(url, { headers, signal: controller.signal });
      clearTimeout(timeoutId);

      if (!res.ok) return null;
      const json = await res.json();
      const card = json?.data?.[0];
      if (!card) return null;

      // STRICT VALIDATION - AVOID FALLBACKS:
      // Verify that the returned card actually matches the requested code or card number.
      const cardReturnedCode = String(card.code || card.number || card.external_id || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const cardReturnedSetCode = String(card.set?.set_code || card.set_code || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

      const isMatch = (cardReturnedCode && (
        cardReturnedCode === normalizedReqCode ||
        cardReturnedCode.includes(normalizedReqCode) ||
        normalizedReqCode.includes(cardReturnedCode)
      )) || (cardReturnedSetCode && cardReturnedSetCode.includes(normalizedReqCode));

      if (!isMatch) {
        return null;
      }

      let rawPrice: number | null = null;
      if (card.price !== undefined && card.price !== null && !isNaN(Number(card.price)) && Number(card.price) > 0) {
        rawPrice = Number(card.price);
      } else if (Array.isArray(card.variants) && card.variants.length > 0) {
        const validPrices = card.variants
          .map((v: any) => v?.price ?? v?.marketPrice ?? v?.set_price ?? v?.midPrice ?? v?.avgPrice ?? v?.lowPrice)
          .filter((p: any) => p !== undefined && p !== null && !isNaN(Number(p)) && Number(p) > 0)
          .map((p: any) => Number(p));
        if (validPrices.length > 0) {
          rawPrice = Math.min(...validPrices);
        }
      }

      if (rawPrice !== null && !isNaN(Number(rawPrice)) && Number(rawPrice) > 0) {
        return Math.round(Number(rawPrice) * 5 * 100) / 100; // Convert USD to BRL factor 5 rounded to 2 decimal places
      }
      return null;
    } catch {
      return null;
    }
  };

  try {
    // 1. Try exact code
    const p = await fetchAttempt(cleanCode);
    if (p !== null && p > 0) return p;

    // 2. Try normalized hyphen variant (e.g. OP01-001 <-> OP01001)
    if (cleanCode.includes('-')) {
      const pNoHyphen = await fetchAttempt(cleanCode.replace(/-/g, ''));
      if (pNoHyphen !== null && pNoHyphen > 0) return pNoHyphen;
    } else {
      const withHyphen = cleanCode.replace(/^([A-Za-z0-9]{4})(\d{3})$/, '$1-$2');
      if (withHyphen !== cleanCode) {
        const pWithHyphen = await fetchAttempt(withHyphen);
        if (pWithHyphen !== null && pWithHyphen > 0) return pWithHyphen;
      }
    }
  } catch {
    // no fallback
  }

  // Absolutely NO fallbacks to bare character name or arbitrary id!
  return null;
};

/**
 * Retorna o preço estimado da carta em BRL.
 * Diretrizes:
 * - A alteração de preço no banco é responsabilidade externa (não alteramos o valor no banco).
 * - Se já estiver em cache ou atualizado hoje, retorna imediatamente.
 * - Prioriza dados confiáveis e evita qualquer fallback aleatório.
 */
export const getCardPrice = async (
  game: number | string | undefined | null,
  codeOrExtId: string | undefined | null,
  name?: string,
  cardDbId?: number | string | null,
  existingPriceUpdatedAt?: string | Date | null,
  existingPrice?: number | null
): Promise<number> => {
  const rawCode = (codeOrExtId || '').trim();
  // Filter out internal database integer IDs from being treated as card codes
  const code = /^\d{1,4}$/.test(rawCode) ? '' : rawCode;
  const slug = resolveGameSlug(game);

  // 1. Verificação rápida: Se o chamador já forneceu um preço atualizado hoje, alimenta o cache e retorna
  if (
    isPriceUpdatedToday(existingPriceUpdatedAt) && 
    existingPrice !== null && 
    existingPrice !== undefined && 
    !isNaN(Number(existingPrice)) &&
    Number(existingPrice) > 0
  ) {
    const validPrice = Number(existingPrice);
    setPriceInCache(slug, validPrice, existingPriceUpdatedAt ? new Date(existingPriceUpdatedAt).toISOString() : new Date().toISOString(), code, cardDbId);
    return validPrice;
  }

  // 2. Verifica se já temos o preço atualizado hoje no cache (memória ou localStorage)
  const cached = getPriceFromCache(slug, code, cardDbId);
  if (cached && isPriceUpdatedToday(cached.price_updated_at) && cached.price > 0) {
    return cached.price;
  }

  // 3. Consulta no banco: verifica se há valor registrado na tabela 'cards'
  let dbPrice: number | null = null;
  let dbUpdatedAt: string | null = null;
  try {
    let query = supabase.from('cards').select('id, price, price_updated_at');
    if (cardDbId) {
      query = query.eq('id', Number(cardDbId));
    } else if (code) {
      const numericGameId = Number(game);
      if (!isNaN(numericGameId) && numericGameId > 0) {
        query = query.eq('game_id', numericGameId).eq('external_id', code);
      } else {
        query = query.eq('external_id', code);
      }
    }

    const { data: dbCards } = await query.limit(1);
    const dbCard = dbCards && dbCards.length > 0 ? dbCards[0] : null;

    if (dbCard) {
      if (dbCard.price !== null && dbCard.price !== undefined && !isNaN(Number(dbCard.price)) && Number(dbCard.price) > 0) {
        // cards.price no Supabase é mantido em Dólar (USD). Converte para Real (BRL) multiplicando pelo fator 5.
        dbPrice = convertUsdToBrl(Number(dbCard.price));
        dbUpdatedAt = dbCard.price_updated_at || null;
      }
      if (!cardDbId && dbCard.id) {
        cardDbId = dbCard.id;
      }

      // Se o banco já possui valor atualizado hoje pela rotina externa, usa convertido e alimenta o cache
      if (dbPrice !== null && dbPrice > 0 && isPriceUpdatedToday(dbUpdatedAt)) {
        setPriceInCache(slug, dbPrice, dbUpdatedAt || new Date().toISOString(), code, cardDbId);
        return dbPrice;
      }
    }
  } catch (err) {
    console.warn('Erro ao consultar preço da carta no banco:', err);
  }

  // 4. Busca o valor mais recente na API externa (Homura / Kyubey) SOMENTE com código estrito verificado
  let fetchedPrice: number | null = null;
  if (code && code.trim()) {
    fetchedPrice = await fetchPriceFromExternalApi(slug, code);
  }

  // Preço final estrito:
  // - Se a API retornou preço estrito para o código, usa
  // - Senão se o banco tem preço (> 0), usa o banco
  // - Senão se existingPrice for válido (> 0), usa existingPrice
  // - Senão 0 (NÃO usa fallbacks aleatórios de nomes ou IDs)
  const rawFinalPrice = (fetchedPrice !== null && fetchedPrice > 0)
    ? fetchedPrice
    : (dbPrice !== null && dbPrice > 0 ? dbPrice : (existingPrice !== null && existingPrice !== undefined && Number(existingPrice) > 0 ? Number(existingPrice) : 0));
  const finalPrice = Math.round(rawFinalPrice * 100) / 100;

  // 5. Atualiza o cache se o preço for positivo
  const nowIso = new Date().toISOString();
  if (finalPrice > 0) {
    setPriceInCache(slug, finalPrice, nowIso, code, cardDbId);
  }

  return finalPrice;
};

/**
 * Concurrency helper for running async tasks with a limit
 */
async function runWithConcurrency<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let currentIndex = 0;

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (currentIndex < items.length) {
      const idx = currentIndex++;
      try {
        results[idx] = await fn(items[idx]);
      } catch {
        results[idx] = 0 as unknown as R;
      }
    }
  });

  await Promise.all(workers);
  return results;
}

export interface CardPriceItem {
  id?: number | string | null;           // Database cards.id
  card_id?: number | string | null;      // Reference to cards.id
  card_db_id?: number | string | null;   // Explicit card database id
  game_id?: number | string | null;
  game?: string | number | null;
  code?: string | null;
  external_id?: string | null;
  name?: string | null;
  quantity?: number | null;
  price?: number | null;
  price_updated_at?: string | Date | null;
  variant_number?: number | null;
}

/**
 * Calculates the total estimated value for a set of card items in a folder or binder.
 * Uses stored card prices without triggering automatic external price updates.
 */
export const calculateFolderEstimatedValue = async (
  items: CardPriceItem[],
  _concurrency = 5,
  _preferStoredPrices = true
): Promise<number> => {
  if (!items || items.length === 0) return 0;

  const total = items.reduce((sum, item) => {
    const qty = typeof item.quantity === 'number' && item.quantity >= 0 ? item.quantity : 1;
    const price = typeof item.price === 'number' && !isNaN(item.price) && item.price > 0 ? item.price : 0;
    return sum + (price * qty);
  }, 0);

  return Math.round(total * 100) / 100;
};

/**
 * Enriches a list of cards with their estimated prices.
 * Respects the daily update rule: if already updated today in the DB, leaves as-is without processing.
 */
export const populateCardPrices = async <T extends CardPriceItem>(
  cards: T[],
  concurrency = 5
): Promise<(T & { price: number })[]> => {
  if (!cards || cards.length === 0) return [];

  const prices = await runWithConcurrency(cards, concurrency, async (item) => {
    // Fast path: if already updated today in the DB, NÃO PROCESSA NADA!
    if (
      isPriceUpdatedToday(item.price_updated_at) && 
      item.price !== null && 
      item.price !== undefined && 
      !isNaN(Number(item.price))
    ) {
      return Number(item.price);
    }

    const cardCode = item.external_id || item.code;
    const cardGame = item.game_id || item.game;
    const cardName = item.name || undefined;
    const cardDbId = item.id || item.card_id || item.card_db_id;

    return await getCardPrice(
      cardGame, 
      cardCode, 
      cardName, 
      cardDbId, 
      item.price_updated_at, 
      item.price
    );
  });

  return cards.map((c, i) => {
    const rawP = prices[i] !== undefined ? prices[i] : (Number(c.price) || 0);
    return {
      ...c,
      price: Math.round(Number(rawP) * 100) / 100
    };
  });
};

/**
 * Fetches rich full details for a card from Homura API.
 */
export const fetchCardFullDetails = async (
  game: number | string | undefined | null,
  codeOrExtId: string | undefined | null
): Promise<any | null> => {
  const rawCode = (codeOrExtId || '').trim();
  if (!rawCode || /^\d{1,4}$/.test(rawCode)) {
    return null;
  }

  const slug = resolveGameSlug(game);
  const token = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_HOMURA_TOKEN) || 
                (typeof process !== 'undefined' && process.env?.VITE_HOMURA_TOKEN) || '';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const normalizedReqCode = rawCode.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  const fetchAttempt = async (paramVal: string) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const apiBase = getApiBaseUrlForSlug(slug);
      const url = `${apiBase}/api/${slug}/cards?code=${encodeURIComponent(paramVal)}&limit=1`;
      const res = await fetch(url, { headers, signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) return null;
      const json = await res.json();
      const card = json?.data?.[0];
      if (!card) return null;

      const cardReturnedCode = String(card.code || card.number || card.external_id || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const cardReturnedSetCode = String(card.set?.set_code || card.set_code || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

      const isMatch = (cardReturnedCode && (
        cardReturnedCode === normalizedReqCode ||
        cardReturnedCode.includes(normalizedReqCode) ||
        normalizedReqCode.includes(cardReturnedCode)
      )) || (cardReturnedSetCode && cardReturnedSetCode.includes(normalizedReqCode));

      return isMatch ? card : null;
    } catch {
      return null;
    }
  };

  const byCode = await fetchAttempt(rawCode);
  if (byCode) return byCode;

  if (rawCode.includes('-')) {
    const byNoHyphen = await fetchAttempt(rawCode.replace(/-/g, ''));
    if (byNoHyphen) return byNoHyphen;
  } else {
    const withHyphen = rawCode.replace(/^([A-Za-z0-9]{4})(\d{3})$/, '$1-$2');
    if (withHyphen !== rawCode) {
      const byHyphen = await fetchAttempt(withHyphen);
      if (byHyphen) return byHyphen;
    }
  }

  return null;
};
